import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  IconButton,
  Text,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  Button,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  MinusIcon,
  RepeatIcon,
  ExternalLinkIcon,
} from "@chakra-ui/icons";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { ROUTE_NAMES } from "@root/constants";
import { useLocation } from "react-router-dom";
import { useProjectsContext } from "@root/context/projects-context";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DrawingPdfViewerProps {
  pdfUrl: string;
  payload: any;
}

const DrawingPdfViewer: React.FC<DrawingPdfViewerProps> = ({
  pdfUrl,
  payload,
}) => {
  const { t } = useTranslation("common");
  const {
    pdfPageNumber: pageNumber,
    setPdfPageNumber: setPageNumber,
    onPageChange,
  } = useMainChatContext();
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);

  // Drag/Pan state
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const { pathname, state } = useLocation();

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log("PDF loaded successfully with", numPages, "pages");
    console.log("PDF URL:", pdfUrl);
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onThumbnailDocumentLoadSuccess = () => {
    // Check scroll state after thumbnails are loaded
    setTimeout(checkScrollState, 200);
    setTimeout(checkScrollState, 800);

    // Also use requestAnimationFrame for more precise timing
    requestAnimationFrame(() => {
      setTimeout(checkScrollState, 100);
    });
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    console.error("PDF URL that failed:", pdfUrl);
    setLoading(false);
    setError(error.message);
  };

  const { fetchDocument } = useProjectsContext();

  const retryLoading = () => {
    if (state?.file) {
      fetchDocument(state?.file?.id);
    } else if (payload) {
      setPageNumber(payload?.page_no);
      fetchDocument(payload?.pdf_id);
    }
    setLoading(true);
    setError(null);
  };

  const goToPreviousPage = () => {
    const newPage = Math.max(pageNumber - 1, 1);
    setPageNumber(newPage);
    onPageChange?.(newPage);
  };

  const goToNextPage = () => {
    const newPage = Math.min(pageNumber + 1, numPages);
    setPageNumber(newPage);
    onPageChange?.(newPage);
  };

  const goToPage = (pageNum: number) => {
    const newPage = Math.max(1, Math.min(pageNum, numPages));
    setPageNumber(newPage);
    onPageChange?.(newPage);
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const rotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const resetView = () => {
    setScale(1);
    setRotation(0);
    setPanOffset({ x: 0, y: 0 }); // Reset pan position
  };

  // Drag/Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStart) return;

    e.preventDefault();
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    setPanOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // Touch handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !dragStart || e.touches.length !== 1) return;

    e.preventDefault();
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.x;
    const deltaY = touch.clientY - dragStart.y;

    setPanOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));

    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // Thumbnail scroll functions
  const scrollThumbnailsLeft = () => {
    if (thumbnailScrollRef.current) {
      thumbnailScrollRef.current.scrollBy({
        left: -120, // Scroll by approximately one thumbnail width + spacing
        behavior: "smooth",
      });
    }
  };

  const scrollThumbnailsRight = () => {
    if (thumbnailScrollRef.current) {
      thumbnailScrollRef.current.scrollBy({
        left: 120, // Scroll by approximately one thumbnail width + spacing
        behavior: "smooth",
      });
    }
  };

  // Check scroll state
  const checkScrollState = () => {
    if (thumbnailScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        thumbnailScrollRef.current;
      const canScrollLeftNew = scrollLeft > 0;
      const canScrollRightNew = scrollLeft < scrollWidth - clientWidth - 1; // -1 for precision

      // Debug logging
      console.log("Scroll state check:", {
        scrollLeft,
        scrollWidth,
        clientWidth,
        canScrollLeft: canScrollLeftNew,
        canScrollRight: canScrollRightNew,
      });

      setCanScrollLeft(canScrollLeftNew);
      setCanScrollRight(canScrollRightNew);
    }
  };

  // Effect to check scroll state when thumbnails load
  useEffect(() => {
    if (numPages > 0) {
      // Use multiple timeouts to ensure thumbnails are fully rendered
      const timeouts = [
        setTimeout(checkScrollState, 100),
        setTimeout(checkScrollState, 500),
        setTimeout(checkScrollState, 1000),
      ];

      return () => {
        timeouts.forEach(clearTimeout);
      };
    }
  }, [numPages]);

  // Effect to check scroll state on window resize
  useEffect(() => {
    const handleResize = () => {
      checkScrollState();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Effect to prevent text selection during drag
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging]);

  if (error) {
    return (
      <VStack spacing={4} p={4}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {t("drawing_analysis.failedToLoadPdf", { error })}
        </Alert>
        <Button bgColor={"#40599B"} onClick={retryLoading} size="sm">
          {t("drawing_analysis.retryLoadingPdf")}
        </Button>
        <Text fontSize="sm" color="gray.600">
          {t("drawing_analysis.ifIssuePersists")}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t("drawing_analysis.pdfUrlAccessibility")}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t("drawing_analysis.networkConnection")}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {t("drawing_analysis.pdfFileSize")}
        </Text>
        <Text fontSize="sm" color="gray.600" mt={2}>
          {t("drawing_analysis.currentUrl", { url: pdfUrl })}
        </Text>
      </VStack>
    );
  }

  return (
    <Box padding={"6px"} height={"100%"}>
      <VStack
        spacing={"20px"}
        width="100%"
        height="100%"
        bgColor={"#CDD6DC33"}
        borderRadius={"20px"}
        padding={"40px 20px"}
      >
        {/* Main PDF Viewer */}
        <Box
          width="100%"
          flex={1}
          overflow="hidden"
          position="relative"
          ref={pdfContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          cursor={isDragging ? "grabbing" : "grab"}
          userSelect="none"
          bg="gray.50"
          borderRadius="md"
        >
          {/* Drag instruction overlay */}
          {!isDragging && (panOffset.x !== 0 || panOffset.y !== 0) && (
            <Box
              position="absolute"
              top="10px"
              left="50%"
              transform="translateX(-50%)"
              bg="rgba(64, 89, 155, 0.9)"
              color="white"
              px={3}
              py={1}
              borderRadius="md"
              fontSize="sm"
              zIndex={5}
              pointerEvents="none"
            >
              {t("drawing_analysis.dragToPan")}
            </Box>
          )}

          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            transform={`translate(${panOffset.x}px, ${panOffset.y}px)`}
            transition={isDragging ? "none" : "transform 0.1s ease-out"}
            opacity={isDragging ? 0.9 : 1}
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              className={"document-main"}
              loading={
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <Text>{t("drawing_analysis.loadingPdf")}</Text>
                </Box>
              }
            >
              <Box
                border={"1px dashed #40599B"}
                borderRadius={"16px"}
                overflow={"visible"}
                display="inline-block"
                minW="fit-content"
                minH="fit-content"
                bg="white"
                boxShadow="lg"
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            </Document>
          </Box>
        </Box>

        <Box width={"100%"}>
          {/* Thumbnails Section */}
          {numPages > 0 && (
            <Box
              position="relative"
              width="100%"
              marginBottom={"30px"}
              px={"40px"}
            >
              {/* Left Scroll Arrow */}
              {canScrollLeft && (
                <Box
                  position="absolute"
                  left="0"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={10}
                  bg="white"
                  borderRadius="50%"
                  boxShadow="lg"
                  border="1px solid #40599B"
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: "xl",
                  }}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={scrollThumbnailsLeft}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="40px"
                  height="40px"
                >
                  <ChevronLeftIcon color="#40599B" boxSize={6} />
                </Box>
              )}

              {/* Right Scroll Arrow */}
              {canScrollRight && (
                <Box
                  position="absolute"
                  right="0"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={10}
                  bg="white"
                  borderRadius="50%"
                  boxShadow="lg"
                  border="1px solid #40599B"
                  _hover={{
                    bg: "gray.50",
                    transform: "translateY(-50%) scale(1.1)",
                    boxShadow: "xl",
                  }}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={scrollThumbnailsRight}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="40px"
                  height="40px"
                >
                  <ChevronRightIcon color="#40599B" boxSize={6} />
                </Box>
              )}

              {/* Thumbnails Container */}
              <Box
                ref={thumbnailScrollRef}
                width="100%"
                overflowX="auto"
                borderRadius="md"
                onScroll={checkScrollState}
                css={{
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#40599B",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#2d3f6b",
                  },
                }}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onThumbnailDocumentLoadSuccess}
                  loading={
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="60px"
                      width="100%"
                    >
                      <Text>{t("drawing_analysis.loadingThumbnails")}</Text>
                    </Box>
                  }
                >
                  <HStack spacing={2} minW="fit-content" flexShrink={0} py={2}>
                    {Array.from(new Array(numPages), (_, index) => (
                      <Box
                        key={index + 1}
                        cursor="pointer"
                        border={
                          pageNumber === index + 1 ? "2px solid" : "1px solid"
                        }
                        borderColor={
                          pageNumber === index + 1 ? "#40599B" : "gray.300"
                        }
                        borderRadius="md"
                        overflow="hidden"
                        onClick={() => goToPage(index + 1)}
                        _hover={{
                          borderColor: "blue.300",
                          transform: "scale(1.05)",
                          boxShadow: "md",
                        }}
                        transition="all 0.2s"
                        bg="white"
                        boxShadow="sm"
                        height={"60px"}
                        width={"100px"}
                        flexShrink={0}
                      >
                        <Page
                          pageNumber={index + 1}
                          scale={0.25}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Box>
                    ))}
                  </HStack>
                </Document>
              </Box>
            </Box>
          )}

          {/* Controls Section */}
          {numPages > 0 && (
            <Flex
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Left Side - Zoom and Rotation Controls */}
              <HStack
                spacing={3}
                alignItems="center"
                bgColor={"white"}
                border={"1px solid #40599B"}
                borderRadius={"60px"}
                p={"5px 20px"}
              >
                {/* Zoom Controls Group */}
                <HStack spacing={1} alignItems="center">
                  <IconButton
                    aria-label={t("drawing_analysis.zoomOut")}
                    icon={<MinusIcon />}
                    onClick={zoomOut}
                    size="sm"
                    variant="ghost"
                    isDisabled={scale <= 0.5}
                    color="gray.700"
                    _hover={{ bg: "gray.100" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />

                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    minWidth="40px"
                    textAlign="center"
                    color="gray.700"
                    userSelect="none"
                  >
                    {Math.round(scale * 100)}%
                  </Text>

                  <IconButton
                    aria-label={t("drawing_analysis.zoomIn")}
                    icon={<AddIcon />}
                    onClick={zoomIn}
                    size="sm"
                    variant="ghost"
                    isDisabled={scale >= 3}
                    color="gray.700"
                    _hover={{ bg: "gray.100" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />
                </HStack>

                {/* Separator Line */}
                <Box width="1px" height="20px" bg="gray.300" mx={2} />

                {/* Rotation and Reset Controls */}
                <HStack spacing={2} alignItems="center">
                  <IconButton
                    aria-label={t("drawing_analysis.rotate")}
                    icon={<RepeatIcon />}
                    onClick={rotate}
                    size="sm"
                    variant="ghost"
                    color="gray.700"
                    _hover={{ bg: "gray.100" }}
                  />

                  <Box
                    onClick={resetView}
                    cursor={"pointer"}
                    color="gray.700"
                    _hover={{ color: "#40599B", transform: "scale(1.1)" }}
                    transition="all 0.2s"
                    title={t("drawing_analysis.resetView")}
                  >
                    ▣
                  </Box>
                </HStack>
              </HStack>

              {/* Right Side - Pagination */}
              <HStack
                spacing={1}
                alignItems="center"
                bgColor={"white"}
                border={"1px solid #40599B"}
                borderRadius={"60px"}
                p={"5px 20px"}
              >
                <IconButton
                  aria-label={t("drawing_analysis.previousPage")}
                  icon={<ChevronLeftIcon />}
                  onClick={goToPreviousPage}
                  size="sm"
                  variant="ghost"
                  isDisabled={pageNumber <= 1}
                  color="gray.700"
                  _hover={{ bg: "gray.100" }}
                  _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                />

                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  minWidth="50px"
                  textAlign="center"
                  color="gray.700"
                  userSelect="none"
                >
                  {pageNumber} / {numPages}
                </Text>

                <IconButton
                  aria-label={t("drawing_analysis.nextPage")}
                  icon={<ChevronRightIcon />}
                  onClick={goToNextPage}
                  size="sm"
                  variant="ghost"
                  isDisabled={pageNumber >= numPages}
                  color="gray.700"
                  _hover={{ bg: "gray.100" }}
                  _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                />
              </HStack>
            </Flex>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default DrawingPdfViewer;
